"use client";

import Player from "@vimeo/player";
import { useEffect, useRef, useState } from "react";

type ImmersiveVimeoPlayerProps = {
  title: string;
  embedUrl: string;
  className?: string;
  soundButtonClassName?: string;
};

const coverVideoClassName =
  "absolute left-1/2 top-1/2 h-[56.25vw] min-h-full min-w-full w-[177.77777778vh] -translate-x-1/2 -translate-y-1/2";

function SoundIcon({ muted }: { muted: boolean }) {
  return muted ? (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z" />
      <path d="m16 9 5 6" />
      <path d="m21 9-5 6" />
    </svg>
  ) : (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z" />
      <path d="M15.5 9.5a4.5 4.5 0 0 1 0 5" />
      <path d="M18.5 7a8 8 0 0 1 0 10" />
    </svg>
  );
}

export function ImmersiveVimeoPlayer({
  title,
  embedUrl,
  className = "",
  soundButtonClassName = "",
}: ImmersiveVimeoPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!iframeRef.current) {
      return;
    }

    const player = new Player(iframeRef.current);
    playerRef.current = player;

    let isDisposed = false;

    async function initializePlayer() {
      try {
        await player.ready();

        if (isDisposed) {
          return;
        }

        await player.setMuted(true);
        try {
          await player.setVolume(0);
        } catch {}
        try {
          await player.play();
        } catch {}
        setIsMuted(true);
        setIsReady(true);
      } catch {
        if (!isDisposed) {
          setIsReady(false);
        }
      }
    }

    void initializePlayer();

    return () => {
      isDisposed = true;
      setIsReady(false);
      setIsMuted(true);
      playerRef.current = null;

      void (async () => {
        try {
          await player.setMuted(true);
        } catch {}

        try {
          await player.pause();
        } catch {}

        try {
          await player.destroy();
        } catch {}
      })();
    };
  }, [embedUrl]);

  async function handleSoundToggle() {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    try {
      if (isMuted) {
        await player.setMuted(false);
        setIsMuted(false);
        try {
          await player.setVolume(1);
        } catch {}
        try {
          await player.play();
        } catch {}
        return;
      }

      await player.setMuted(true);
      setIsMuted(true);
      try {
        await player.setVolume(0);
      } catch {}
    } catch {}
  }

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className={coverVideoClassName}
      />

      <button
        type="button"
        onClick={() => void handleSoundToggle()}
        aria-label={isMuted ? "Turn sound on for video" : "Turn sound off for video"}
        aria-pressed={!isMuted}
        disabled={!isReady}
        className={`absolute bottom-4 right-4 z-20 inline-flex items-center gap-2 rounded-full border border-white/18 bg-[#021006]/78 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_18px_40px_-18px_rgba(0,0,0,0.8)] backdrop-blur-md transition hover:bg-[#03210c]/88 disabled:cursor-not-allowed disabled:opacity-70 ${soundButtonClassName}`}
      >
        <SoundIcon muted={isMuted} />
        <span>{isMuted ? "Sound off" : "Sound on"}</span>
      </button>
    </div>
  );
}
